/* eslint-disable import/no-default-export */
import Cookie from 'js-cookie';
import getAudienceDefinitions from './audience-definitions';
import CollapsedTest from './collapsed-test';
import SelectedVariation from './selected-variation';
import { AudienceCheck, AudienceDefinitions, Test, Variation } from './types';
const customError = require('@atlassian/trello-error-ext');

const InvalidExperimentName = customError('SimpleTest::InvalidExperimentName');
const InvalidExperimentAllocation = customError(
  'SimpleTest::InvalidExperimentAllocation',
);

// These tests have already started running with the old bucketing method,
// so we can't change them now but we don't want any new tests to use this
// method.

const V1_EXPERIMENTS = [
  'npsSurveyV3',
  'powerUpRequestCTAV2',
  'teamMemberTabLayoutV1',
];

export default class SimpleTest {
  audienceDefinitions: AudienceDefinitions;

  constructor(audienceDefinitions: ReturnType<typeof getAudienceDefinitions>) {
    this.audienceDefinitions = audienceDefinitions;
  }

  selectNamed(test: Test, name: string) {
    return SelectedVariation.variationsAndControl(test).find(
      (variation: Variation) => variation.name === name,
    );
  }

  checkAudiences(test: Test) {
    const { audiences } = test;
    if (!audiences) {
      return true;
    }

    return audiences.some((audience) => {
      return (this.audienceDefinitions[audience] as AudienceCheck)(
        test.audienceSettings,
      );
    });
  }

  getParams(testName: string, identifier: string) {
    const test = window.abTests ? window.abTests[testName] : undefined;

    if (!test) {
      throw InvalidExperimentName();
    }

    test.name = test.name || testName;

    if (typeof test.defaultWeight === 'undefined') {
      test.defaultWeight = 1;
    }

    test.experimentVersion =
      (test.experimentVersion && test.experimentVersion.toUpperCase()) || '';

    if (typeof test.experimentAllocation === 'number') {
      if (test.experimentAllocation > 1 || test.experimentAllocation < 0) {
        throw InvalidExperimentAllocation();
      }
    } else if (typeof test.experimentAllocation === 'object') {
      const hasInvalidAllocation = Object.values(
        test.experimentAllocation,
      ).some((value: string) => !(Number(value) >= 0 && Number(value) <= 1));
      if (hasInvalidAllocation) {
        throw InvalidExperimentAllocation();
      }
    } else {
      throw InvalidExperimentAllocation();
    }

    // Check the cookies for a force_<experiment> cookie, with the value being the variation to run.
    // e.g. document.cookie='force_inlinePowerUpV1=inlineText''
    let override = Cookie.get(`force_${test.name}`);
    // Check for a force_experiments_control cookie, for automated testing
    const forceControlCookie = Cookie.get('force_experiments_control');

    if (!override && forceControlCookie && forceControlCookie !== '0') {
      console.log(
        `${
          test.name
        } experiment forced to control with "force_experiments_control" cookie`,
      );
      override = 'control';
    } else if (override) {
      console.log(
        `${test.name} experiment forced to ${override} with "force_${
          test.name
        }" cookie`,
      );
    }

    const bucketVersion = V1_EXPERIMENTS.includes(
      `${test.name}${test.experimentVersion}`,
    )
      ? 'v1'
      : 'v2';

    let selectedVariation;
    if (override) {
      selectedVariation = this.selectNamed(test, override);
    } else if (this.checkAudiences(test)) {
      selectedVariation = new SelectedVariation(this.audienceDefinitions)[
        bucketVersion
      ](test, identifier);
    }

    const collapsedTest = new CollapsedTest(
      test,
      selectedVariation,
      identifier,
    );

    if (selectedVariation && test.autoTrack) {
      collapsedTest.track();
    }

    return collapsedTest;
  }
}



// WEBPACK FOOTER //
// ./app/common/lib/experiments/simple-test.ts